import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getAllCategories,
  addCategory,
  updateCategory,
  deleteCategory,
} from '../../services/categoryService';

// Async thunks for category operations

/**
 * Fetch all categories from Firestore
 */
export const fetchCategories = createAsyncThunk(
  'category/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const categories = await getAllCategories();
      return categories;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Create a new category
 */
export const createCategory = createAsyncThunk(
  'category/createCategory',
  async (categoryData, { rejectWithValue }) => {
    try {
      const categoryId = await addCategory(categoryData);
      return { id: categoryId, ...categoryData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Update an existing category
 */
export const updateCategoryById = createAsyncThunk(
  'category/updateCategory',
  async ({ categoryId, categoryData }, { rejectWithValue }) => {
    try {
      await updateCategory(categoryId, categoryData);
      return { id: categoryId, ...categoryData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Delete a category
 */
export const deleteCategoryById = createAsyncThunk(
  'category/deleteCategory',
  async (categoryId, { rejectWithValue }) => {
    try {
      await deleteCategory(categoryId);
      return categoryId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Update category status (active/disabled)
 */
export const updateCategoryStatus = createAsyncThunk(
  'category/updateCategoryStatus',
  async ({ categoryId, status }, { rejectWithValue }) => {
    try {
      await updateCategory(categoryId, { status });
      return { id: categoryId, status };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  categories: [],
  loading: false,
  error: null,
  success: false,
};

// Category slice
const categorySlice = createSlice({
  name: 'category',
  initialState,
  reducers: {
    // Clear error and success messages
    clearMessages: (state) => {
      state.error = null;
      state.success = false;
    },
    // Reset category state
    resetCategoryState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch categories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
        state.error = null;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch categories';
      })

      // Create category
      .addCase(createCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.categories.unshift(action.payload);
        state.success = true;
        state.error = null;
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create category';
        state.success = false;
      })

      // Update category
      .addCase(updateCategoryById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateCategoryById.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.categories.findIndex((cat) => cat.id === action.payload.id);
        if (index !== -1) {
          state.categories[index] = { ...state.categories[index], ...action.payload };
        }
        state.success = true;
        state.error = null;
      })
      .addCase(updateCategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update category';
        state.success = false;
      })

      // Delete category
      .addCase(deleteCategoryById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteCategoryById.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = state.categories.filter((cat) => cat.id !== action.payload);
        state.success = true;
        state.error = null;
      })
      .addCase(deleteCategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete category';
        state.success = false;
      })

      // Update category status
      .addCase(updateCategoryStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateCategoryStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.categories.findIndex((cat) => cat.id === action.payload.id);
        if (index !== -1) {
          state.categories[index].status = action.payload.status;
        }
        state.success = true;
        state.error = null;
      })
      .addCase(updateCategoryStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update category status';
        state.success = false;
      });
  },
});

export const { clearMessages, resetCategoryState } = categorySlice.actions;
export default categorySlice.reducer;
